// @ts-strict-ignore
import { effect, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATION } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, map, mergeMap, take } from 'rxjs';
import {
  ApiListTransitionStoryGroupsResponse,
  ApiTransitionElementResponse,
  ApiTransitionStoryGroupResponse,
} from '../../api/core/models';
import { ActionsService, WatchlistService } from '../../api/core/services';
import { ModelV2Service } from '../../services/backend-service/model-v2.service';
import { BoardFacadeService } from '../../services/store/board/board-facade.service';
import { selectRouteBoardId } from '../router/router.selectors';
import { CustomRouterNavigationAction } from '../router/router.types';
import { TransitionStoriesStore } from '../transition-stories/transition-stories.store';
import { actionsEffectsActions, actionsFacadeActions, outcomeAssessmentsActions } from './actions.actions';

@Injectable()
export class ActionsEffects {
  private _transitionElementIds: string[] = [];

  constructor(
    private _actions$: Actions,
    private _actionsService: ActionsService,
    private _boardFacadeService: BoardFacadeService,
    private _modelV2Service: ModelV2Service,
    private _watchlistService: WatchlistService,
    private _store: Store,
    private _transitionStoriesStore: TransitionStoriesStore
  ) {
    effect(() => {
      // if a Transition Element is deleted, any outcome assessments associated with it should also be deleted from the state to match what happens in the backend
      const hiearchy: ApiListTransitionStoryGroupsResponse = this._transitionStoriesStore.transitionStoryHierarchy();
      if (hiearchy) {
        let transitionElementIds: string[] = [];
        for (const group of hiearchy.transitionStoryGroups ?? []) {
          transitionElementIds = transitionElementIds.concat(this._collectTransitionElementIds(group));
        }
        const deletedIds = this._transitionElementIds.filter((id) => !transitionElementIds.includes(id));
        deletedIds.forEach((deletedId) => {
          _store.dispatch(
            outcomeAssessmentsActions.deleteTransitionElementSuccess({
              transitionElementId: deletedId,
            })
          );
        });
        this._transitionElementIds = transitionElementIds;
      }
    });
  }

  routeBoardIdChanged$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CustomRouterNavigationAction>(ROUTER_NAVIGATION),
      map((navigationData) => navigationData.payload.routerState.params),
      filter(({ boardId, organisationId }) => !!boardId && !!organisationId),
      distinctUntilChanged((prev, curr) => prev.boardId === curr.boardId),
      map(() => {
        return actionsEffectsActions.resetState();
      })
    )
  );

  loadActions$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(actionsFacadeActions.loadActions),
        mergeMap(() => {
          return combineLatest([
            this._store.select(selectRouteBoardId),
            this._boardFacadeService.getBoardProperty$(['id']),
            this._boardFacadeService.boardLanguage$,
          ]).pipe(
            filter(([routeBoardId, boardId, language]) => !!boardId && !!language && boardId === routeBoardId),
            take(1),
            mergeMap(([_, boardId, language]) => {
              return combineLatest([
                this._actionsService.listActions({ boardId: boardId!, 'Accept-Language': language }),
                this._watchlistService.listWatchlist({ boardId: boardId!, 'Accept-Language': language }),
              ]).pipe(
                filter(([actionsResponse, watchlistResponse]) => {
                  return Boolean(actionsResponse?.actions) && Boolean(watchlistResponse?.actionWatchlist);
                }),
                take(1),
                map(([actionsResponse, watchlistResponse]) => {
                  const actions = actionsResponse?.actions ?? [];
                  const watchlist = watchlistResponse?.actionWatchlist ?? [];

                  const clientActions = actions.map((action) =>
                    this._modelV2Service.toClientActionFromActionResponse(action, watchlist)
                  );

                  return actionsEffectsActions.loadedActions({ actionsList: clientActions });
                })
              );
            })
          );
        })
      );
    },
    { functional: true }
  );

  deletedAction$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(actionsEffectsActions.deleteActionSuccess),
        map(({ actionId }) => {
          return outcomeAssessmentsActions.deleteActionSuccess({
            actionId,
          });
        })
      );
    },
    { functional: true }
  );

  private _collectTransitionElementIds(group: ApiTransitionStoryGroupResponse): string[] {
    let ids: string[] = [];
    if (group.transitionElements) {
      ids = ids.concat(group.transitionElements.map((el: ApiTransitionElementResponse) => el.id));
    }
    if (group.subTransitionStoryGroups) {
      for (const subGroup of Object.values(group.subTransitionStoryGroups)) {
        ids = ids.concat(this._collectTransitionElementIds(subGroup));
      }
    }
    return ids;
  }
}
